import React, { createContext, useState, useContext, useEffect } from 'react';
import { onAuthStateChanged, signOut as firebaseSignOut, isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { Navigate, useLocation } from 'react-router-dom';
import firebase from 'lib/firebase';
import { useConnection } from '../hooks/useConnection';
import { CircularProgress, Alert, Box, Button, Typography } from '@mui/material';
import { claimGuestSignalsForUser } from '../utils/guestSignalLedger';

const AuthContext = createContext(null);
const AUTH_RESOLUTION_TIMEOUT_MS = 4000;
const CLAIMS_RESOLUTION_TIMEOUT_MS = 6000;

const withTimeout = (promise, timeoutMs, message) => new Promise((resolve, reject) => {
  const timeoutId = window.setTimeout(() => reject(new Error(message)), timeoutMs);
  Promise.resolve(promise).then(
    (value) => {
      window.clearTimeout(timeoutId);
      resolve(value);
    },
    (error) => {
      window.clearTimeout(timeoutId);
      reject(error);
    }
  );
});

const buildMockClaims = (mockUser = {}) => ({
  role: mockUser.role || 'fan',
  roles: Array.isArray(mockUser.roles) ? mockUser.roles : undefined,
  admin: mockUser.admin === true,
  operator: mockUser.operator === true,
  radar_operator: mockUser.radar_operator === true,
});

const clearCommunicationsView = (uid) => {
  if (!uid || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(`togeevision:communications:v1:${uid}:last-view`);
  } catch (_) {
    // Storage is optional; authorization never depends on it.
  }
};

// Protected Route Component
export const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, userRole, loading, claimsLoading } = useAuth();
  const location = useLocation();
  const e2eAuthBypass = process.env.REACT_APP_E2E_AUTH_BYPASS === 'true';

  if (loading || claimsLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        gap={1.5}
        justifyContent="center"
        alignItems="center"
        minHeight="40vh"
        sx={{ bgcolor: '#000', color: '#cbd5e1', px: 2, textAlign: 'center' }}
      >
        <CircularProgress />
        <Box>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800 }}>
            Restoring secure session
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.25 }}>
            Rétablissement de la session sécurisée
          </Typography>
        </Box>
      </Box>
    );
  }

  if (e2eAuthBypass) {
    return children;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRole && (!userRole || !requiredRole.includes(userRole))) {
    return <Navigate to="/unauthorized" state={{ from: location }} replace />;
  }

  return children;
};

export const AuthProvider = ({ children }) => {
  const e2eAuthBypass = process.env.REACT_APP_E2E_AUTH_BYPASS === 'true';
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [claims, setClaims] = useState({});
  const [claimsLoading, setClaimsLoading] = useState(!e2eAuthBypass);
  const [loading, setLoading] = useState(!e2eAuthBypass);
  const [error, setError] = useState(null);
  const { isConnected, isOnline } = useConnection();

  const setMockSession = (mockUser) => {
    if (mockUser) {
      const mockClaims = buildMockClaims(mockUser);
      setUser(mockUser);
      setClaims(mockClaims);
      setUserRole(mockClaims.role);
      setClaimsLoading(false);
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem('togeevision.mock_user', JSON.stringify(mockUser));
          claimGuestSignalsForUser(mockUser);
        } catch (e) {
          console.warn('[auth] Unable to save mock user:', e);
        }
      }
    } else {
      const departingUid = user?.uid;
      setUser(null);
      setUserRole(null);
      setClaims({});
      setClaimsLoading(false);
      clearCommunicationsView(departingUid);
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.removeItem('togeevision.mock_user');
        } catch (e) {
          console.warn('[auth] Unable to remove mock user:', e);
        }
      }
    }
  };

  useEffect(() => {
    if (e2eAuthBypass) {
      setUser({ uid: 'e2e-mobile-user', email: 'mobile-layout@togeevision.test' });
      setClaims({ role: 'admin', admin: true });
      setUserRole('admin');
      setClaimsLoading(false);
      setLoading(false);
      return undefined;
    }

    // Magic link (passwordless email) completion detection
    if (firebase.auth && typeof window !== 'undefined' && isSignInWithEmailLink(firebase.auth, window.location.href)) {
      const storedEmail = window.localStorage.getItem('togeevision.magicLinkEmail');
      if (storedEmail) {
        signInWithEmailLink(firebase.auth, storedEmail, window.location.href)
          .then(() => {
            window.localStorage.removeItem('togeevision.magicLinkEmail');
            // Clean up the sign-in link from the URL without losing query params
            const url = new URL(window.location.href);
            ['oobCode', 'mode', 'apiKey', 'lang'].forEach(p => url.searchParams.delete(p));
            window.history.replaceState({}, '', url.pathname + url.search);
          })
          .catch((err) => {
            console.error('[auth] Magic link sign-in failed:', err);
            window.localStorage.removeItem('togeevision.magicLinkEmail');
          });
      }
    }

    // Check for mock user in localStorage on mount
    if (typeof window !== 'undefined') {
      try {
        const storedMock = window.localStorage.getItem('togeevision.mock_user');
        if (storedMock) {
          const parsed = JSON.parse(storedMock);
          if (parsed && parsed.uid) {
            const mockClaims = buildMockClaims(parsed);
            setUser(parsed);
            setClaims(mockClaims);
            setUserRole(mockClaims.role);
            setClaimsLoading(false);
            setLoading(false);
            
            // If Firebase is NOT configured or auth is missing, we don't need onAuthStateChanged.
            if (!firebase.isConfigured || !firebase.auth) {
              return undefined;
            }
          }
        }
      } catch (e) {
        console.warn('[auth] Error reading mock user from localStorage:', e);
      }
    }

    let unsubscribe;
    const initializationTimeout = window.setTimeout(() => {
      console.warn('[auth] Firebase session resolution timed out; releasing the public application shell.');
      setClaimsLoading(false);
      setLoading(false);
    }, AUTH_RESOLUTION_TIMEOUT_MS);

    if (!firebase.isConfigured || !firebase.auth) {
      if (!firebase.isConfigured) {
        console.warn('[auth] Firebase configuration missing, skipping auth initialization.');
      }
      setClaimsLoading(false);
      setLoading(false);
      return undefined;
    }

    unsubscribe = onAuthStateChanged(firebase.auth, async (nextUser) => {
      window.clearTimeout(initializationTimeout);
      setLoading(true);
      setClaimsLoading(true);
      if (nextUser) {
        setUser(nextUser);
        try {
          const tokenResult = await withTimeout(
            nextUser.getIdTokenResult(/* forceRefresh= */ false),
            CLAIMS_RESOLUTION_TIMEOUT_MS,
            'Firebase permission verification timed out.'
          );
          const authoritativeClaims = tokenResult?.claims || {};
          setClaims(authoritativeClaims);
          setUserRole(authoritativeClaims.role || null);
          claimGuestSignalsForUser(nextUser);
          setError(null);
        } catch (err) {
          console.error('Error reading token claims:', err);
          setClaims({});
          setUserRole(null);
          setError('Unable to verify user permissions.');
        }
      } else {
        // Firebase has no authenticated user. Check if we have a mock user session active in localStorage.
        let hasMock = false;
        if (typeof window !== 'undefined') {
          try {
            const storedMock = window.localStorage.getItem('togeevision.mock_user');
            if (storedMock) {
              const parsed = JSON.parse(storedMock);
              if (parsed && parsed.uid) {
                const mockClaims = buildMockClaims(parsed);
                setUser(parsed);
                setClaims(mockClaims);
                setUserRole(mockClaims.role);
                hasMock = true;
              }
            }
          } catch (_) {}
        }
        if (!hasMock) {
          setUser(null);
          setUserRole(null);
          setClaims({});
        }
      }

      setClaimsLoading(false);
      setLoading(false);
    });

    return () => {
      window.clearTimeout(initializationTimeout);
      if (unsubscribe) unsubscribe();
    };
  }, [e2eAuthBypass]);


  const handleRetry = async () => {
    if (!isConnected) {
      setError('Still offline. Please check your connection.');
      return;
    }

    setLoading(true);
    setClaimsLoading(true);
    setError(null);

    if (firebase.auth?.currentUser) {
      try {
        const tokenResult = await withTimeout(
          firebase.auth.currentUser.getIdTokenResult(true),
          CLAIMS_RESOLUTION_TIMEOUT_MS,
          'Firebase permission refresh timed out.'
        );
        const authoritativeClaims = tokenResult?.claims || {};
        setClaims(authoritativeClaims);
        setUserRole(authoritativeClaims.role || null);
      } catch (err) {
        setError('Failed to refresh user permissions. Please try again.');
      }
    }
    setClaimsLoading(false);
    setLoading(false);
  };

  const signOut = async () => {
    try {
      if (firebase.auth) {
        await firebaseSignOut(firebase.auth);
      }
    } catch (err) {
      console.error('Sign out error:', err);
    } finally {
      setMockSession(null);
    }
  };

  return (
    <AuthContext.Provider 
      value={{
        user,
        userRole,
        claims,
        claimsLoading,
        loading,
        error,
        setError,
        isConnected,
        retry: handleRetry,
        signOut,
        setMockSession,
      }}
    >
      {!isOnline && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              Retry
            </Button>
          }
        >
          You are currently offline. Some live information may be unavailable.<br />
          Vous êtes actuellement hors ligne. Certaines informations en direct peuvent être indisponibles.
        </Alert>
      )}
      {isOnline && error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
